import { type ReactNode, useEffect, useState } from 'react';
import { customerApi } from '../customer_api';
import type { BrandResponse } from './brand';
import { CustomerBrandContext, type CustomerBrandState } from './brandContext';
import { SpaceNounContext } from './spaceNounContext';

/**
 * Root-level provider for the customer portal's tenant branding. Fetches
 * `GET /api/customer/branding` ONCE per page load and provides:
 *
 *   - `CustomerBrandContext` — the whole response (tenant name, primary
 *     colour, …) for the persistent `CustomerPortalHeader` and for
 *     `BrandedShell`, which reads it instead of re-fetching.
 *   - `SpaceNounContext` — the tenant "space" noun (migration 0097) so account
 *     pages that don't sit inside a `BrandedShell` still say "Pen" for a
 *     cattery.
 *
 * Fails soft: until the fetch resolves (or if it fails) consumers get
 * platform defaults.
 */
export function CustomerBrandProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<CustomerBrandState>({ brand: null, loaded: false });

  useEffect(() => {
    let cancelled = false;
    customerApi<BrandResponse>('/api/customer/branding')
      .then((b) => {
        if (!cancelled) setState({ brand: b, loaded: true });
      })
      .catch(() => {
        // Tenant not resolved / offline — keep the defaults.
        if (!cancelled) setState({ brand: null, loaded: true });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <CustomerBrandContext.Provider value={state}>
      <SpaceNounContext.Provider value={state.brand?.spaceNoun ?? null}>
        {children}
      </SpaceNounContext.Provider>
    </CustomerBrandContext.Provider>
  );
}
